import { useShallow } from 'zustand/react/shallow';
import { useExportStore } from '@/hooks/data/export/useExportStore';
import { TaskStateBar } from '@/components/task-state-bar';
import { isDefined } from '@/utilities/guards';
import { TaskState } from '@/types/uploads';

const isTerminal = (state: TaskState) => state === TaskState.Finished || state === TaskState.Errored;

/**
 * Renders a single export's bar. Subscribes to *its own* export only, so a WS
 * progress tick on one export re-renders only that bar — not every bar. This is
 * safe because `updateExport` maps over the array, preserving the object
 * reference of unchanged exports, so this selector returns a referentially-equal
 * value (no re-render) when a sibling updates.
 *
 * - onFinished auto-dismisses the Finished (✓) bar after a short delay, matching
 *   batch bars. The bar is purely informational once complete — the download
 *   comes from the ArchiveReady notification, not the bar — so it need not linger.
 *   (Errored bars do not auto-dismiss; the user dismisses those manually.)
 * - onCancel maps to removeExport and is only enabled on terminal states — there
 *   is no backend abort endpoint, so there is nothing to cancel mid-progress.
 * - No sub-tasks: we never list the files inside the zip, so TaskStateBar renders
 *   the row as non-expandable (no chevron, inert trigger) automatically.
 */
const ExportTaskBar = ({ exportId }: { exportId: string }) => {
  const exportTask = useExportStore((state) => state.exports.find((item) => item.id === exportId));
  const removeExport = useExportStore((state) => state.removeExport);

  if (!isDefined(exportTask)) {
    return null;
  }

  return (
    <TaskStateBar
      open={false}
      taskId={exportTask.id}
      state={exportTask.state}
      progress={exportTask.progress}
      title={exportTask.label}
      onFinished={() => removeExport(exportTask.id)}
      onCancel={isTerminal(exportTask.state) ? () => removeExport(exportTask.id) : undefined}
    />
  );
};

/**
 * Renders one bar per active export, in the sidebar's shared Accordion after the
 * upload and batch bars. Subscribes only to the list of export *ids* (shallow-
 * compared), so it re-renders when exports are added/removed but not on progress
 * ticks — each ExportTaskBar handles its own updates.
 */
export const ExportTaskBars = () => {
  const exportIds = useExportStore(useShallow((state) => state.exports.map((item) => item.id)));

  return (
    <>
      {exportIds.map((exportId) => (
        <ExportTaskBar key={exportId} exportId={exportId} />
      ))}
    </>
  );
};
